import { FC, MouseEvent, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CAMPAIGNS_ROUTE,
  UPDATE_CAMPAIGN_BASE_ROUTE,
} from 'src/constants/routes'
import { getDate } from 'src/utils/date'
import en from 'src/constants/en'
import { useUpdateCampaignMutation } from 'src/reactQuery/useUpdateCampaignMutation'
import { ReactComponent as Loudspeaker } from 'src/assets/svg/loudspeaker.svg'
import { CampaignsChart } from 'src/pages/Campaigns/CampaignsChart'
import MenuThreeDotsIcon from 'src/assets/svg/menuThreeDots.svg'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter'
import { CampaignType } from 'src/services/__generated__/api'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { numberWithCommas } from 'src/utils/numberFormat'
import { ReactComponent as ShieldIcon } from './svg/brand-managment.svg'
import { ReactComponent as CompetitorIcon } from './svg/competitor-analysis.svg'
import { ReactComponent as MarketIcon } from './svg/market-reasearch.svg'
import { ReactComponent as ReviewIcon } from './svg/review-analysis.svg'
import styles from './campaignCard.module.scss'

const CATEGORY_CONFIG: Record<
  string,
  { bg: string; color: string; icon: FC<React.SVGProps<SVGSVGElement>> }
> = {
  'Brand Management': {
    bg: '#8080FF33',
    color: '#8080FF',
    icon: ShieldIcon,
  },
  'Competitor Analysis': {
    bg: '#8080FF33',
    color: '#8080FF',
    icon: CompetitorIcon,
  },
  'Market Research': {
    bg: '#8080FF33',
    color: '#8080FF',
    icon: MarketIcon,
  },
  'Review Analysis': {
    bg: '#8080FF33',
    color: '#8080FF',
    icon: ReviewIcon,
  },
}

interface CampaignCardProps {
  campaignRefetch: () => void
  data: CampaignType
  locationRefetch?: () => void
  onDelete: () => void
  onDuplicate: () => void
  onReactivate?: () => void
  onRename: () => void
  positiveRefetch?: () => void
  sourcesRefetch?: () => void
}

const CampaignCard: FC<CampaignCardProps> = ({
  data,
  campaignRefetch,
  positiveRefetch,
  locationRefetch,
  sourcesRefetch,
  onDelete,
  onDuplicate,
  onRename,
  onReactivate,
}) => {
  const dropDownRef = useRef<HTMLDivElement>(null)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  useOutsideClick({
    onOutsideClick: () => {
      setIsDropdownOpen(false)
    },
    ref: dropDownRef,
  })

  const navigate = useNavigate()

  const themeDetailLink = useCustomLink(`${CAMPAIGNS_ROUTE}/${data?._id}`)

  const updatePageLink = useCustomLink(
    `${UPDATE_CAMPAIGN_BASE_ROUTE}/${data?._id}`
  )

  const campaignMutation = useUpdateCampaignMutation({
    onSuccess() {
      campaignRefetch()
      positiveRefetch?.()
      locationRefetch?.()
      sourcesRefetch?.()
    },
    updateType: 'complete',
  })

  const {
    keywordsCount,
    negativeMentionsCount,
    neutralMentionsCount,
    positiveMentionsCount,
    sources: topSources,
    topKeywords,
  } = data?.stats ?? {}

  const navigateToDetailPage = (event: MouseEvent<HTMLDivElement>) => {
    if (
      (event.target as HTMLDivElement).id !== 'dropdownMenuButton' &&
      (event.target as HTMLDivElement).id !== 'deleteButtonId' &&
      (event.target as HTMLDivElement).id !== 'updateButtonId'
    ) {
      navigate(themeDetailLink)
    }
  }

  const completeCampaign = () => {
    const campaignData = { ...data }
    delete campaignData._id

    campaignMutation.mutate({
      data: {
        ...campaignData,
        status: 'completed',
        to: new Date().toISOString(),
      },
      id: data._id,
    })
  }

  const reactivateCampaign = () => {
    onReactivate?.()
    setIsDropdownOpen(false)
  }

  return (
    <div className={styles.campaignCard} onClick={navigateToDetailPage}>
      <div className={styles.cardTitleRow}>
        <div className={styles.campaignNameAndCategories}>
          <span className={styles.campaignName}>{data?.name}</span>
        </div>

        {/* Show menu for all campaigns */}
        <div className={styles.dropdown} ref={dropDownRef}>
          <button
            className={styles.dropdownButton}
            onClick={(e) => {
              e.stopPropagation()

              setIsDropdownOpen((isDropdownOpen) => !isDropdownOpen)
            }}
          >
            <img alt="menu icon" src={MenuThreeDotsIcon} />
          </button>

          {isDropdownOpen && (
            <div
              className={styles.dropdownMenu}
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <div className={styles.dropdownItem} onClick={() => onRename()}>
                {en.rename}
              </div>

              {data?.status?.toLowerCase() === 'completed' ? (
                <div
                  className={styles.dropdownItem}
                  onClick={reactivateCampaign}
                >
                  Reactivate
                </div>
              ) : data?.status?.toLowerCase() === 'active' ? (
                <div className={styles.dropdownItem} onClick={completeCampaign}>
                  {en.complete}
                </div>
              ) : null}

              <Link className={styles.dropdownItem} to={updatePageLink}>
                {en.edit}
              </Link>

              <div
                className={styles.dropdownItem}
                onClick={() => onDuplicate()}
              >
                {en.duplicateCampaign}
              </div>

              <div className={styles.dropdownItem} onClick={() => onDelete()}>
                {en.delete}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.metaRow}>
        <span className={styles.metaLabel}>{en.keywords}</span>
        <span className={styles.metaDot}>•</span>
        <span className={styles.metaCount}>{keywordsCount}</span>
        <span className={styles.metaDivider}>|</span>
        <span className={styles.metaLabel}>Tags</span>
        <span className={styles.metaDot}>•</span>
        <span className={styles.metaCount}>{data?.tags?.length || 0}</span>
      </div>

      <div className={styles.pillsRow}>
        <div className={styles.statusPill}>
          {capitalizeFirstLetter(data?.status ?? '')}
        </div>
        {data?.categories && data.categories.length > 0 && (
          <div
            className={styles.categoryPill}
            style={{
              background:
                CATEGORY_CONFIG[data.categories[0]]?.bg ?? '#8080FF33',
              color: CATEGORY_CONFIG[data.categories[0]]?.color ?? '#8080FF',
            }}
          >
            {(() => {
              const Icon =
                CATEGORY_CONFIG[data.categories[0]]?.icon ?? ShieldIcon
              return <Icon className={styles.categoryPillIcon} />
            })()}
            <span>{data.categories[0]}</span>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.contentWrapper}>
          <div className={styles.mentionsAndTopSources}>
            {!!topSources?.length && (
              <div className={styles.section}>
                <div className={styles.title}>{en.topSources}</div>

                <div className={styles.sectionList}>
                  {topSources?.slice(0, 3).map(({ source, mentions }) => (
                    <div
                      className={styles.sectionListItem}
                      key={`${source}-${mentions}`}
                    >
                      <span className={styles.sourceIcon}>
                        {source && SOCIAL_ICONS[source.toLowerCase()]}
                      </span>

                      <span className={styles.label}>
                        {platformDisplayLabel(
                          source ?? '',
                          capitalizeFirstLetter(source ?? '')
                        )}{' '}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!topKeywords?.length && (
              <div className={styles.section}>
                <div className={styles.title}>{en.topKeywords}</div>

                <div className={styles.sectionList}>
                  {topKeywords?.map((keywordItem) => (
                    <div
                      className={styles.sectionListItem}
                      key={`${keywordItem.keyword}-${keywordItem.mentions}`}
                    >
                      <span className={styles.label}>
                        {keywordItem.keyword}
                        &nbsp;•&nbsp;
                      </span>
                      <span className={styles.labelCount}>
                        {numberWithCommas(keywordItem.mentions || 0)}
                      </span>
                      &nbsp;
                      <Loudspeaker className={styles.loudSpeakerIcon} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartWrapper}>
          <CampaignsChart
            negativeMentionsCount={negativeMentionsCount || 0}
            neutralMentionsCount={neutralMentionsCount || 0}
            positiveMentionsCount={positiveMentionsCount || 0}
          />
        </div>
      </div>

      <div className={styles.dates}>
        {data?.createdAt && `Created at ${getDate(data?.createdAt)}`}

        <span>|</span>

        {data?.updatedAt && `Updated at ${getDate(data?.updatedAt)}`}
      </div>
    </div>
  )
}

export default CampaignCard
